import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper, type ReactNodeViewProps } from "@tiptap/react";
import { EmbedPreview } from "./EmbedPreview";
import type { EmbedItem } from "../articleStatus";

/**
 * TipTap node extension that renders embeds as non-editable islands
 * inside the editor. Users can see the embed preview but can't edit
 * its contents — only reposition it by dragging or cut/paste.
 */

function EmbedNodeView(props: ReactNodeViewProps) {
  const raw = props.node.attrs.embedData as string;
  let embed: EmbedItem | null = null;
  try {
    embed = JSON.parse(raw) as EmbedItem;
  } catch {
    return null;
  }

  if (!embed) return null;

  return (
    <NodeViewWrapper className="embed-node" data-drag-handle="">
      <EmbedPreview embed={embed} />
    </NodeViewWrapper>
  );
}

export const EmbedExtension = Node.create({
  name: "embedBlock",
  group: "block",
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      embedData: {
        default: "{}",
        parseHTML: (el) => el.getAttribute("data-embed"),
        renderHTML: (attrs) => ({ "data-embed": attrs.embedData as string }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div[data-embed]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { class: "embed-node" })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(EmbedNodeView);
  },
});

/**
 * Inject embed markers into HTML content at the correct paragraph positions.
 * This converts the separate embeds array into inline `<div data-embed="...">` tags
 * that TipTap can parse as EmbedBlock nodes.
 */
export function injectEmbedsIntoHTML(html: string, embeds: EmbedItem[] | undefined): string {
  if (!embeds?.length || !html) return html;

  // Filter out featured image and junk embeds (same logic as BodyWithEmbeds)
  const JUNK_URL_PATTERNS =
    /\b(logo|icon|badge|avatar|favicon|button|banner-ad|pixel|tracker|google-news|newss?\.svg)\b/i;
  const filtered = embeds.filter((e) => {
    if (e.is_featured_candidate) return false;
    if (e.url.toLowerCase().endsWith(".svg")) return false;
    if (JUNK_URL_PATTERNS.test(e.url)) return false;
    if (e.alt && JUNK_URL_PATTERNS.test(e.alt)) return false;
    return true;
  });
  if (!filtered.length) return html;

  // Split at block boundaries
  const parts = html.split(/(<\/(?:p|h[1-6])>)/i);

  // Build paragraph texts for anchor matching
  const paragraphTexts: string[] = [];
  let textAccum = "";
  for (const part of parts) {
    textAccum += part;
    if (/^<\/(?:p|h[1-6])>$/i.test(part)) {
      paragraphTexts.push(
        textAccum
          .replace(/<[^>]+>/g, " ")
          .replace(/\s+/g, " ")
          .toLowerCase()
          .trim(),
      );
      textAccum = "";
    }
  }

  function resolveIdx(embed: EmbedItem): number {
    if (embed.preceding_text) {
      const anchor = embed.preceding_text.toLowerCase().slice(0, 60).trim();
      if (anchor.length >= 10) {
        const found = paragraphTexts.findIndex((t) => t.includes(anchor));
        if (found >= 0) return found + 1;
      }
    }
    return embed.paragraph_index ?? 0;
  }

  // Build embed map
  const embedMap = new Map<number, EmbedItem[]>();
  for (const embed of filtered) {
    const idx = resolveIdx(embed);
    const list = embedMap.get(idx) || [];
    list.push(embed);
    embedMap.set(idx, list);
  }

  function embedToTag(embed: EmbedItem): string {
    const data = JSON.stringify(embed).replace(/"/g, "&quot;");
    return `<div data-embed="${data}"></div>`;
  }

  let result = "";
  let pCount = 0;

  // Embeds at position 0
  const atZero = embedMap.get(0);
  if (atZero) result += atZero.map(embedToTag).join("");

  let accum = "";
  for (const part of parts) {
    accum += part;
    if (/^<\/(?:p|h[1-6])>$/i.test(part)) {
      pCount++;
      result += accum;
      accum = "";
      const embs = embedMap.get(pCount);
      if (embs) result += embs.map(embedToTag).join("");
    }
  }
  if (accum.trim()) result += accum;

  return result;
}

/**
 * Strip embed markers from HTML before saving.
 * Embeds are stored separately in extracted_metadata, not in the body HTML.
 */
export function stripEmbedsFromHTML(html: string): string {
  return html.replace(/<div[^>]*data-embed="[^"]*"[^>]*><\/div>/g, "");
}
